import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthenticationForms from "../AuthenticationForms";
import { useTranslation } from "react-i18next";

import "./style.css"
import { handleAuthSuccess } from "../../../utils";
import { Context } from "../../../context";
import QrStep from "./QrStep";
import OTPStep from "./OTPStep";
import SmallSpinner from "src/component/SmallSpinner/SmallSpinner";
import { Confirm2FA, Initiate2FA, Verify2FACode } from "src/requests/twoFactor";

const twoFactorLength = 6;

const TwoFactorSteps = {
    requiresEnrollment: 1,
    requires2Fa: 2
}

interface TwoFactorState {
    requiresEnrollment?: boolean;
    requires2Fa?: boolean;
    enrollmentToken?: string;
    challengeToken?: string;
}

interface InitQr {
    qrPngBase64: string;
    secretBase32: string;
}

const getInitialStep = (state: TwoFactorState | null) => {
    if (state?.requiresEnrollment) return TwoFactorSteps.requiresEnrollment;
    if (state?.requires2Fa) return TwoFactorSteps.requires2Fa;
    return TwoFactorSteps.requiresEnrollment;
};

const TwoFactorAuthentication = () => {
    const [isQrLoading, setIsQrLoading] = useState(false);

    const { setIsLoggedIn } = useContext(Context);
    const { state } = useLocation() as { state: TwoFactorState | null };
    const { t } = useTranslation();
    const navigate = useNavigate()

    const [step, setStep] = useState(() => getInitialStep(state));
    const [otp, setOtp] = useState<string[]>(Array(twoFactorLength).fill(""));
    const [initQr, setInitQr] = useState<InitQr | null>(null);
    const [verifyError, setVerifyError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (!state) navigate("/login")

        setStep(getInitialStep(state));

        if (!state?.requiresEnrollment || !state?.enrollmentToken) return;

        setIsQrLoading(true);
        Initiate2FA(state.enrollmentToken)
            .then((res: any) => {
                if (res.data.success) {
                    setInitQr(res.data.data);
                }
            })
            .finally(() => {
                setIsQrLoading(false);
            });
    }, [state, navigate]);

    useEffect(() => {
        if (otp.every((d) => d !== "")) {
            handleSubmit();
        }
    }, [otp]);

    const handleNext = () => setStep(TwoFactorSteps.requires2Fa);

    const handleSubmit = async () => {
        const code = otp.join("");

        if (code.length !== twoFactorLength) return;

        try {
            let result: any;
            setIsSubmitting(true)
            if (state?.requiresEnrollment) {
                result = await Confirm2FA(state.enrollmentToken!, { code });
            } else {
                result = await Verify2FACode(state!.challengeToken!, { code });
            }

            if (result?.data?.success) {
                handleAuthSuccess({
                    data: result.data.data,
                    setIsLoggedIn,
                });
            }
        } catch (error: any) {
            setVerifyError(error?.response?.data?.message)
        } finally {
            setIsSubmitting(false)
        }
    };

    return (
        <AuthenticationForms>
            <div className="d-flex justify-content-center flex-column align-items-center gap-3">

                <div className="login-content-header">
                    <span className="login-content-header-title">
                        {step === TwoFactorSteps.requiresEnrollment
                            ? t("2faEnrollmentTitle")
                            : t("2faTitle")}
                    </span>

                    <div className="login-content-header-caption">
                        <span>
                            {step === TwoFactorSteps.requiresEnrollment
                                ? t("2faScanInstruction")
                                : t("2faCaption")}
                        </span>
                        <span className="d-block text-center">
                            {step === TwoFactorSteps.requiresEnrollment
                                && t("2faAppExamples")
                            }
                        </span>
                    </div>
                </div>

                {step === TwoFactorSteps.requiresEnrollment && state?.requiresEnrollment && (
                    <>
                        {isQrLoading ? (
                            <div className="qr-loader d-flex flex-column align-items-center gap-2">
                                <SmallSpinner className="fs-1 text-white" />
                            </div>
                        ) : (
                            <QrStep handleNext={handleNext} initQr={initQr} />
                        )}
                    </>
                )}

                {(step === TwoFactorSteps.requires2Fa || state?.requires2Fa) && (
                    <OTPStep
                        handleSubmit={handleSubmit}
                        otp={otp}
                        setOtp={setOtp}
                        error={verifyError}
                        setVerifyError={setVerifyError}
                        isSubmitting={isSubmitting}
                    />
                )}

            </div>
        </AuthenticationForms>
    );
};

export default TwoFactorAuthentication;
